import { UserPlus, Search, MessageSquare, Gamepad2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const steps = [
  { num: 1, icon: UserPlus, title: 'Create Your Profile', desc: 'Sign up and tell us about your Mahjong experience, availability, and preferences. Our multi-step form makes it easy.', color: '#56dfb4' },
  { num: 2, icon: Search, title: 'Find Local Players', desc: 'Use our search filters to find compatible players near you. Filter by skill level, availability, distance, and more.', color: '#56c6df' },
  { num: 3, icon: MessageSquare, title: 'Connect & Message', desc: 'Send messages to players you\'d like to play with. Coordinate schedules and arrange your game.', color: '#df5681' },
  { num: 4, icon: Gamepad2, title: 'Play Mahjong!', desc: 'Meet up at your chosen location and enjoy a game of American Mahjong with your new group.', color: '#b456df' },
];

export default function HowItWorksPage() {
  return (
    <div data-testid="how-it-works-page" className="py-16 lg:py-24">
      <SEO
        title="How It Works — Find Mahjong Players in 4 Steps"
        description="Create a profile, search nearby Mahjong players, message them in-site, and play. See how Meet For Mahjong connects you with local American Mahjong groups."
        path="/how-it-works"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#56c6df]">How It Works</span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-3 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Four Simple Steps
          </h1>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto">
            Getting started with Meet For Mahjong is easy. Here's how it works.
          </p>
        </div>

        <div className="space-y-12">
          {steps.map((step, i) => (
            <div key={step.num} className={`flex flex-col md:flex-row gap-6 items-start ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center relative" style={{ backgroundColor: `${step.color}15` }}>
                  <step.icon className="w-7 h-7" style={{ color: step.color }} />
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#0f172a] text-white text-xs font-bold flex items-center justify-center">
                    {step.num}
                  </span>
                </div>
              </div>
              <div className="flex-1 p-6 rounded-2xl bg-white border border-slate-100 shadow-sm">
                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                <p className="text-slate-500 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center p-10 rounded-3xl bg-[#56dfb4]/5 border border-[#56dfb4]/20">
          <h3 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to Get Started?</h3>
          <p className="text-slate-500 mb-6">Create your free account and start finding local Mahjong players today.</p>
          <Link to="/register">
            <Button className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] px-8 py-3 font-medium" data-testid="hiw-cta-btn">
              Sign Up Free
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
